/**
 * StructuredData — JSON-LD structured data components for SEO.
 * Renders <script type="application/ld+json"> in <head> via react-helmet-async.
 *
 * Usage:
 *   <OrganizationSchema />           — on homepage (/)
 *   <ArticleSchema article={...} />  — on /estudos/:slug
 *   <SoftwareSchema product={...} /> — on /swim-vision, /trackvision, /reakt
 *   <FAQSchema items={[...]} />      — on landing pages
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

// ─── Organization (homepage) ─────────────────────────────────────────────────
export const OrganizationSchema = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://arenastats.com.br/#organization',
    name: 'VUKZ',
    url: 'https://arenastats.com.br/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://arenastats.com.br/vukz-logo.png',
      width: '512',
      height: '512',
    },
    description: 'Plataforma de inteligência e performance para clubes e equipes esportivas, unificando gestão operacional, treinos, scouting e análise biomecânica com IA.',
    foundingDate: '2024',
    sameAs: [
      'https://www.linkedin.com/company/vukz',
      'https://github.com/vukz-sports',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'suporte@vukz.com.br',
      availableLanguage: 'Portuguese',
    },
    sponsor: {
      '@type': 'Organization',
      name: 'Microsoft for Startups Founders Hub',
      url: 'https://startups.microsoft.com/',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// ─── Article (estudos) ───────────────────────────────────────────────────────
export const ArticleSchema = ({ article }) => {
  if (!article) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://arenastats.com.br/estudos/${article.slug}`,
    },
    headline: article.title,
    description: article.excerpt || article.title,
    image: article.image || 'https://arenastats.com.br/vukz-og-image.png',
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.published_at,
    author: {
      '@type': 'Person',
      name: article.author || 'Especialista em Performance VUKZ',
      worksFor: { '@type': 'Organization', '@id': 'https://arenastats.com.br/#organization' },
    },
    publisher: { '@type': 'Organization', '@id': 'https://arenastats.com.br/#organization' },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// ─── SoftwareApplication (product pages) ─────────────────────────────────────
export const SoftwareSchema = ({ product }) => {
  if (!product) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `https://arenastats.com.br${product.url}#software`,
    name: product.name,
    operatingSystem: 'All',
    applicationCategory: 'BusinessApplication',
    description: product.description,
    offers: {
      '@type': 'Offer',
      price: '0.00',
      priceCurrency: 'BRL',
      description: 'Disponível sob consulta corporativa para clubes, federações e assessorias esportivas.',
    },
    featureList: product.features || [],
    author: { '@type': 'Organization', '@id': 'https://arenastats.com.br/#organization' },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// ─── FAQPage (landing pages) ─────────────────────────────────────────────────
export const FAQSchema = ({ items }) => {
  if (!items || items.length === 0) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
